import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ActionMenu, BasicModal, DataTable } from "@life-line-lab/components-react";
import { cloneDeep, isUndefined, isEmpty, omitBy, isNil } from "lodash";
import moment from "moment";
// shared
import DataTableFilter from "Components/shared/data-table-filter";
import ModalContent from "Components/shared/modal-content";
// helpers
import { encode } from "Helpers/index";
// hooks
import { useSession } from "Hooks/index";
// antd
import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
// types
import {
  ModalTranslationContent,
  ModalType,
  OrgType,
  Statuses,
  Option,
  AccountRole,
} from "Types/common.types";
// scss
import "./user-list.scss";
// json
import sampleUsersData from "./sample-users.json";
import { FetchedUserData, UserRoleToAssign } from "Types/user.types";

interface UsersListProps {
  canList: boolean;
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
  tab?: number;
  usersList: FetchedUserData[];
  companiesList?: Option[];
  customersList?: Option[];
  onSelectOrg?: (orgID: string) => void;
  onFilter?: (filterInput) => void;
  onViewProfile: (orgID: string, profileID: string) => void;
  onDeactivateUser?: (id: string, isDeactivate: boolean, reason: string) => void;
  onActivateUser?: () => void;
}

const UsersList: React.FC<UsersListProps> = ({
  canList,
  canView,
  canEdit,
  canDelete,
  tab,
  usersList,
  companiesList,
  customersList,
  onSelectOrg,
  onFilter,
  onViewProfile,
  onDeactivateUser,
  onActivateUser,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { org } = useSession();

  const orgIsOwner = org.orgType === OrgType.OWNER;
  const orgIsLP = org.orgType === OrgType.LP;
  const orgIsCompany = org.orgType === OrgType.COMPANY;
  const orgIsCustomer = org.orgType === OrgType.CUSTOMER;

  const baseColumns: any = [
    {
      title: t("label.userID").toLocaleUpperCase(),
      dataIndex: ["userRole", "code"],
      // defaultSortOrder: 'ascend',
      // sorter: (a, b) => a.userRole.code > b.userRole.code ? 1 : a.userRole.code < b.userRole.code ? -1 : 0,
      // check this out --> (https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/sort)
      render: (_: any, record: FetchedUserData) => {
        if (canView) {
          const orgID = record.org?.id ?? "";
          const profileID = record.profile?.id ?? "";
          return (
            <a
              className="clickable-id"
              onClick={() => {
                onViewProfile(orgID, profileID);
              }}
            >
              {record.userRole?.code || ""}
            </a>
          );
        }
        return <span>{record.userRole?.code || ""}</span>;
      },
      // onCell: (record:FetchedUserData, rowIndex) => {
      //     if (canView) {
      //         return {
      //             onClick: (e: React.MouseEvent<HTMLElement>) => {
      //                 onViewProfile(record.profile?.id);
      //             }
      //         };
      //     }
      // }
    },
    {
      title: t("label.name").toLocaleUpperCase(),
      dataIndex: ["profile", "fullName"],
      // defaultSortOrder: 'ascend',
      // sorter: (a, b) => a.profile.fullName > b.profile.fullName ? 1 : a.profile.fullName < b.profile.fullName ? -1 : 0,
    },
    {
      title: t("label.role").toLocaleUpperCase(),
      dataIndex: ["userRole", "subType"],
    },
  ];
  const initialModal: any = {
    isOpen: false,
    type: "approve",
    record: {},
  };
  const initialFilter: any = {
    roles: "",
    customersList: "",
    companiesList: "",
  };

  const rolesSelections = useMemo(() => {
    return [
      {
        label: t("option.all"),
        value: "",
      },
      {
        label: t("label.admin"),
        value: UserRoleToAssign.ADMIN,
      },
      {
        label: t("label.superAdmin"),
        value: UserRoleToAssign.SUPER_ADMIN,
      },
      {
        label: t("label.operationSupervisor"),
        value: UserRoleToAssign.OPERATION_SUPERVISOR,
      },
      {
        label: t("label.operationStaff"),
        value: UserRoleToAssign.OPERATIONAL_STAFF,
      },
      {
        label: t("label.publicTransportOperator"),
        value: UserRoleToAssign.PUBLIC_TRANSPORT_OPERATIONAL_USER,
      },
    ];
  }, []);

  const [tableColumns, setTableColumns] = useState(baseColumns);
  const [filter, setFilter] = useState(initialFilter);
  // TODO: enforce type for modal after adding for record in columns
  const [modal, setModal] = useState<any>(initialModal);
  // TODO: enforce types for `filterSelection`
  const initialFilterSelections = [
    {
      key: "Roles",
      name: "roles",
      label: t("label.byRoles"),
      placeholder: t("placeholder.filterByRoles"),
      value: filter?.roles,
      options: rolesSelections,
    },
  ];
  const [filterSelections, setFilterSelections] = useState<any[]>(initialFilterSelections);

  useEffect(() => {
    if (orgIsOwner || orgIsLP) handleAddColumnsForOwnerAndLP();
    if (!orgIsCustomer) {
      handleAddCustomerColumn();
    }
    if (orgIsCompany || orgIsCustomer) {
      handleAddEmailColumn();
      if (!isUndefined(tab)) handleAddActionMenu();
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(customersList) && (orgIsCompany || orgIsLP)) {
      handleAddCustomersFilter();
    }
  }, [customersList]);

  useEffect(() => {
    if (isUndefined(tab)) {
      handleAddDeactivatedTimeColumn();
    }
  }, [tab]);

  useEffect(() => {
    if (onFilter && !isEmpty(filter)) {
      const filterPayload = omitBy(filter, (input) => isNil(input) || isEmpty(input));
      onFilter(filterPayload);
    }
  }, [filter]);

  const handleAddActionMenu = () => {
    const actionMenu = {
      title: "",
      dataIndex: [],
      width: 29,
      render: (_, record: FetchedUserData) => {
        const editActionMenu = [
          {
            label: t("action.viewEdit"),
            icon: <EditOutlined />,
            onClick: () => handleActionClick(record, "EDIT"),
          },
        ];
        const multiActionMenu = [
          {
            label: t("action.viewEdit"),
            icon: <EditOutlined />,
            onClick: () => handleActionClick(record, "EDIT"),
          },
          {
            label: t("action.deactivate"),
            icon: <DeleteOutlined />,
            onClick: () => handleActionClick(record, Statuses.DEACTIVATE),
          },
        ];
        return (
          <ActionMenu
            label={<MoreOutlined />}
            actions={
              record.userRole?.approvalStatus === Statuses.APPROVED
                ? multiActionMenu
                : editActionMenu
            }
          />
        );
      },
    };

    if (canEdit && canDelete) setTableColumns((prevState) => [...prevState, actionMenu]);
  };

  const handleAddCustomersFilter = () => {
    const customersSelection = {
      label: t("label.filterCustomer"),
      key: "customersList",
      name: "customersList",
      placeholder: t("placeholder.filterByCustomer"),
      value: filter?.customersList,
      options: [{ label: t("option.all"), value: "" }].concat(customersList ?? []),
      // options: customersList ?? [],
    };

    setFilterSelections((prevState) => [...prevState, customersSelection]);
  };

  const handleAddCompaniesFilter = () => {
    const companiesSelection = {
      key: "companiesList",
      name: "companiesList",
      label: t("label.filterByCompanies"),
      placeholder: t("placeholder.filterByCompanies"),
      value: filter?.companiesList,
      options: [{ label: t("option.all"), value: "" }].concat(companiesList ?? []),
      // options: companiesList ?? [],
    };

    setFilterSelections((prevState) => [...prevState, companiesSelection]);
  };

  const handleAddDeactivatedTimeColumn = () => {
    const deactivatedTime = {
      title: t("label.deactivatedAt").toLocaleUpperCase(),
      dataIndex: ["userRole", "isDeactivated", "tempDeletedOn"],
      render: (_: any, record: FetchedUserData) => {
        return (
          <React.Fragment>
            {moment(record?.userRole?.isDeactivated?.tempDeletedOn).format("DD-MM-YYYY HH:mm:ss")}
          </React.Fragment>
        );
      },
    };

    if (canEdit && canDelete) setTableColumns((prevState) => [...prevState, deactivatedTime]);
  };

  /**
   * only platform owner and logistic providers has these columns
   */
  const handleAddColumnsForOwnerAndLP = () => {
    let clonedCol = cloneDeep(baseColumns);
    clonedCol = [
      ...clonedCol,
      {
        title: t("label.userType").toLocaleUpperCase(),
        dataIndex: ["org", "userType"],
        record: (record: FetchedUserData) => {
          if (record.org?.orgType === AccountRole.COMPANY) {
            return t("label.companyUser");
          }
          if (record.org?.orgType === AccountRole.COMPANY) {
            return t("label.customerUser");
          }
        },
      },
      {
        title: t("label.customer").toLocaleUpperCase(),
        dataIndex: ["org", "name"],
      },
    ];

    setTableColumns(clonedCol);
  };

  const handleAddCustomerColumn = () => {
    const custCompanyName = {
      title: t("label.companyName").toLocaleUpperCase(),
      dataIndex: ["org", "name"],
    };

    setTableColumns((prevState) => [...prevState, custCompanyName]);
  };

  const handleAddEmailColumn = () => {
    const emailCol = {
      title: t("label.email").toLocaleUpperCase(),
      dataIndex: ["profile", "email"],
    };

    setTableColumns((prevState) => [...prevState, emailCol]);
  };

  const handleActionClick = (record: FetchedUserData, type) => {
    switch (type) {
      case "EDIT": {
        const userID = record.profile?.id && encode(record.profile.id);
        const encodedOrgID = record.org?.id && encode(record.org.id);
        history.push(`/user-management/user/${encodedOrgID}/${userID}/profile`);
        break;
      }
      case Statuses.DEACTIVATE: {
        setModal({ isOpen: true, type: Statuses.DEACTIVATE, record: record });
        break;
      }
      case Statuses.REJECTED: {
        setModal({ isOpen: true, type: Statuses.REJECTED, record: record });
        break;
      }
      case Statuses.APPROVED: {
        setModal({ isOpen: true, type: Statuses.APPROVED, record: record });
        break;
      }
    }
  };

  const handleSearch = (searchInput: string) => {
    setFilter((prevFilter) => ({ ...prevFilter, orFields: searchInput }));
  };

  const handleFilterSelection = (selection) => {
    if (onSelectOrg) {
      // temporary solution, replacing selected company filter value with
      // customer filter value if customer value is not undefined
      // because payload only accept 1 orgID field
      const orgIDSelected = selection.customersList
        ? selection.customersList
        : selection.companiesList;
      onSelectOrg(orgIDSelected);
    }
    if (onFilter) {
      setFilter((prevFilter) => ({ ...prevFilter, roles: selection.roles }));
    }
  };

  // ? write as a helper function
  const handleModalTranslation = (): ModalTranslationContent => {
    if (modal.type === Statuses.DEACTIVATE) {
      return {
        title: t("label.deactivateUser").toUpperCase(),
        sureTxt: t("txt.areYouSureDeactivateAccount"),
        contentToConfirm: t("txt.userID"),
        actionLabel: t("action.deactivate"),
      };
    }
    if (modal.type === Statuses.ACTIVATE) {
      return {
        title: t("label.activateUser").toUpperCase(),
        sureTxt: t("txt.areYouSureActivateAccount"),
        contentToConfirm: t("txt.userID"),
        actionLabel: t("action.activate"),
      };
    }
    return {
      title: "-",
      sureTxt: "-",
      contentToConfirm: "-",
      actionLabel: "-",
    };
  };

  const handleResetModalState = () => {
    setModal(initialModal);
  };

  return (
    <div className="user-list">
      {canList && (
        <DataTable
          rowKey={(record: FetchedUserData) => {
            return record.profile?.id;
          }}
          columns={tableColumns}
          dataSource={usersList}
          title={() => (
            <div className="filter-component">
              <DataTableFilter
                searchPlaceholder={t("label.search")}
                onSearch={(text: string) => {
                  handleSearch(text);
                }}
                onFilter={(selection) => handleFilterSelection(selection)}
                renderFilterSelection={filterSelections}
              />
            </div>
          )}
        />
      )}

      <BasicModal
        isOpen={modal.isOpen}
        isClosable={true}
        isMaskClosable={false}
        onClose={() => {
          handleResetModalState();
        }}
      >
        <ModalContent
          modal={modal}
          onHandleModalTranslation={handleModalTranslation}
          onResetModal={handleResetModalState}
          onDeactivateOrDelete={onDeactivateUser}
          onActivate={onActivateUser}
        />
      </BasicModal>
    </div>
  );
};

export default UsersList;
